import React, { useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Alert,
  Checkbox,
  Typography,
  Space,
  message,
} from 'antd';
import { UserOutlined, LockOutlined, ApartmentOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text } = Typography;

const REMEMBER_KEY = 'bms_remembered_login';

interface LoginFormValues {
  tenantCode: string;
  username: string;
  password: string;
  remember: boolean;
}

// Read remembered Username + Tenant Code (never password) saved on a prior login.
function readRememberedLogin(): { tenantCode: string; username: string } | null {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.username !== 'string' || typeof parsed?.tenantCode !== 'string') return null;
    return { tenantCode: parsed.tenantCode, username: parsed.username };
  } catch {
    return null;
  }
}

interface AccountLockedError {
  code: string;
  remainingMs?: number;
}

function extractErrorCode(error: unknown): string {
  const err = error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> };
  return err?.graphQLErrors?.[0]?.extensions?.code || 'UNKNOWN';
}

function formatLockRemaining(ms: number): string {
  const minutes = Math.ceil(ms / 60000);
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
}

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [lockedError, setLockedError] = useState<AccountLockedError | null>(null);
  const [form] = Form.useForm<LoginFormValues>();

  const remembered = readRememberedLogin();
  const initialValues: Partial<LoginFormValues> = remembered
    ? { tenantCode: remembered.tenantCode, username: remembered.username, remember: true }
    : { remember: false };

  const handleSubmit = async (values: LoginFormValues) => {
    setLoading(true);
    setLockedError(null);
    try {
      const tenantCode = values.tenantCode.trim();
      const username = values.username.trim();
      await login(username, values.password, tenantCode);
      // Persist (or clear) remembered credentials — never the password
      if (values.remember) {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({ tenantCode, username }));
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      // On success: useAuth.login() navigates to /dashboard automatically
    } catch (error: unknown) {
      const code = extractErrorCode(error);
      if (code === 'ACCOUNT_LOCKED') {
        const gqlError = error as { graphQLErrors?: Array<{ extensions?: AccountLockedError }> };
        const ext = gqlError?.graphQLErrors?.[0]?.extensions;
        setLockedError({ code: 'ACCOUNT_LOCKED', remainingMs: ext?.remainingMs });
      } else if (code === 'ACCOUNT_INACTIVE') {
        message.error('Your account is inactive. Please contact your administrator.');
      } else {
        message.error('Invalid credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f2f5',
      }}
    >
      <Card
        style={{ width: 380, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
        variant="borderless"
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              BMS Inventory
            </Title>
            <Text type="secondary">Sign in to your account</Text>
          </div>

          {lockedError && (
            <Alert
              type="error"
              showIcon
              message="Account Locked"
              description={
                lockedError.remainingMs
                  ? `Too many failed attempts. Try again in ${formatLockRemaining(lockedError.remainingMs)}.`
                  : 'Your account is temporarily locked. Please try again later.'
              }
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            initialValues={initialValues}
          >
            <Form.Item
              name="tenantCode"
              rules={[{ required: true, message: 'Please enter your Tenant Code' }]}
            >
              <Input
                prefix={<ApartmentOutlined />}
                placeholder="Tenant Code"
                size="large"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Please enter your username' }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Username"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                size="large"
              />
            </Form.Item>

            <Form.Item name="remember" valuePropName="checked">
              <Checkbox>Remember me</Checkbox>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default LoginPage;
